import React, { useState, useEffect } from "react";
import "./centralIpdType.css";
import { useDispatch, useSelector } from 'react-redux';
import { fetchCentralMedicalManagementData, updateCentralAppointmentStatus } from '../../../_services/centralServices/centralCancelAppointment.service';
import { getTokenFromLocal } from '../../../_helpers/handleResponseMiddleware';
import { toast } from 'react-toastify';
import { CentralModal } from '../CentralModal/CentralModal';
import { CentralSelect } from '../CentralSelect/CentralSelect';
import { useNavigate } from 'react-router-dom';
import { centralReportingDataActions } from "../../../_actions";

export const CentralIpdTypeModal = ({ onClose, hospitalId, selectedIpdType }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const reportingData = useSelector(state => state.centralReportingData);
    const userDetails = useSelector(state => state.authentication.user);
    const [cancelReason, setCancelReason] = useState('');
    const [primaryReason, setPrimaryReason] = useState('');
    const [secondaryReason, setSecondaryReason] = useState('');
    const [reasonsData, setReasonsData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [primaryOptions, setPrimaryOptions] = useState([]);
    const [secondaryOptions, setSecondaryOptions] = useState([]);

    useEffect(() => {
        const fetchData = async () => {

            try {
                const response = await fetchCentralMedicalManagementData();
     
                setReasonsData(response.result.data);
            } catch (error) {
                console.error('Error fetching cancellation reasons:', error);
                toast.error('Failed to fetch cancellation reasons');
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (reasonsData) {
            const activePatientData = reportingData?.reportingData?.data?.find(
                item => item?.appointment_id === reportingData.activePatientId || item._id === reportingData.activePatientId
            );

            if (activePatientData) {
                const ipdTypeChangeReason = activePatientData.ipdTypeChangeReason;
                if (ipdTypeChangeReason) {
        
                    // Ensure ipdTypeChangeReason has the expected structure
                    if (typeof ipdTypeChangeReason === 'object') {
            
                        setCancelReason(ipdTypeChangeReason.ipdTypeReason || '');
                        setPrimaryReason(ipdTypeChangeReason.primaryasIpdTypeReason || '');
                        setSecondaryReason(ipdTypeChangeReason.secondaryIpdTypeReason || '');
                    } else {
                        console.warn('ipdTypeChangeReason is not an object:', ipdTypeChangeReason);
                    }
                } else {
                    console.warn('No IPD type change reason found for active patient data');
                }
            } else {
                console.warn('No active patient data found');
            }
        } else {
            console.warn('Reasons data is not available');
        }
    }, [reportingData, reasonsData]);

    useEffect(() => {
        if (!cancelReason || !reasonsData) {
            setPrimaryOptions([]);
            return;
        }

        const selectedReason = reasonsData.find(reason => reason?.value === cancelReason);
        if (!selectedReason?.options) {
            setPrimaryOptions([]);
            return;
        }

        const options = selectedReason.options
            .filter(option => option && typeof option === 'object' && option.value)
            .map(option => ({
                value: String(option.value || '').trim(),
                label: String(option.value || '').trim()
            }))
            .filter(option => option.value !== '');

        setPrimaryOptions(options);
    }, [cancelReason, reasonsData]);

    useEffect(() => {
        if ( !cancelReason || !primaryReason || !reasonsData) {
            setSecondaryOptions([]);
            return;
        }

        const selectedCancelReason = reasonsData.find(reason => reason?.value === cancelReason);
        console.log('Selected Cancel Reason:', selectedCancelReason);

        if (!selectedCancelReason) {
            console.warn('No matching cancel reason found for:', cancelReason);
            setSecondaryOptions([]);
            return;
        }

        if (typeof selectedCancelReason !== 'object' || !Array.isArray(selectedCancelReason.options)) {
            console.warn('Selected cancel reason is not valid or has no options:', selectedCancelReason);
            setSecondaryOptions([]);
            return;
        }

        const selectedPrimary = selectedCancelReason.options.find(
            option => option?.value === primaryReason
        );

        if (!selectedPrimary) {
            console.warn('No matching primary reason found for:', primaryReason);
            setSecondaryOptions([]);
            return;
        }

        if (!Array.isArray(selectedPrimary.options)) {
            console.warn('Selected primary reason has no options:', selectedPrimary);
            setSecondaryOptions([]);
            return;
        }

        const options = selectedPrimary.options
            .map(option => {
                if (typeof option === 'string') {
                    const value = option.trim();
                    return value ? { value, label: value } : null;
                }
                if (option?.value) {
                    const value = String(option.value).trim();
                    return value ? { value, label: value } : null;
                }
                return null;
            })
            .filter(Boolean);

        setSecondaryOptions(options);
    }, [primaryReason, cancelReason, reasonsData]);

    const handleCancelReasonChange = (e) => {
        const value = e?.target?.value || '';
        setCancelReason(value);
        setPrimaryReason(''); // Clear primary reason when cancel reason changes
        setSecondaryReason(''); // Clear secondary reason when cancel reason changes
    };

    const handlePrimaryReasonChange = (e) => {
        const value = e?.target?.value || '';
        setPrimaryReason(value);
        setSecondaryReason(''); // Clear secondary reason when primary reason changes
    };

    const isDataValid = () => {
        return Boolean(
            cancelReason && 
            primaryReason && 
            secondaryReason
        );
    };

    const handleSimpleClose = () => {
        if (typeof onClose === 'function') {
            onClose();
        }
    };

    const handleSubmit = async () => {
        const user = userDetails || getTokenFromLocal();
        const appId = reportingData?.activePatientId;
        
        const activePatientData = reportingData?.reportingData?.data?.find(
            item => item.appointment_id === appId || item._id === appId
        );
        // const leadId = activePatientData?.lead_id;
    
        if (!appId  || !user?.userId) {
            toast.error('Required appointment data is missing');
            console.error('Missing data:', { appId, userId: user?.userId });
            return;
        }
    
        setIsSubmitting(true);
        try {
            const payload = {
                appId: appId,
                updateObj: {
                    ipdType: selectedIpdType,
                    ipdTypeChangeReason: {
                        actionTakenBy: user?.userId,
                        actionTakenByUser: user?.username || user?.name,
                        dateTimeIso: new Date().toISOString(),
                        ipdTypeReason: cancelReason,
                        primaryasIpdTypeReason: primaryReason,
                        secondaryIpdTypeReason: secondaryReason,
                        source: "hosChatApp"
                    },
                }
            };
    
            const response = await updateCentralAppointmentStatus(payload);
            if (response?.message === 'Success') {
                toast.success('IPD Type reasons updated successfully');
                dispatch(centralReportingDataActions.getCentralReportingData({"search_query":appId}));
            } else {
                toast.error('Failed to update IPD Type reasons');
            }
        } catch (error) {
            console.error('Error updating IPD Type:', error);
            toast.error(error?.message || 'Failed to update IPD Type reasons');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <CentralModal
            modalTitle="Medical Management"
            open={true}
            onClose={handleSimpleClose}
            buttonTitle="Save"
            cancelTitle="Cancel"
            isShowDivider={true}
            isSubmitDisabled={!isDataValid() || isSubmitting}
            onSubmit={handleSubmit}
            onCancel={handleSimpleClose}
            isModalFooterShow={true}
            modalButton={<span></span>}
        >
            <div className="equipmentContentContainer">
                {(!reportingData?.activePatientId || !reportingData?.reportingData?.data?.[0]?.leadId) && (
                    <div className="warning-message">
                        Required appointment data is missing
                    </div>
                )}
                
                <CentralSelect 
                    value={cancelReason}
                    label="Medical Management *"
                    options={
                        Array.isArray(reasonsData) ? 
                            reasonsData
                                .filter(reason => reason && typeof reason === 'object' && reason.value)
                                .map(reason => ({
                                    label: String(reason?.value || '').trim(),
                                    value: String(reason?.value || '').trim()
                                }))
                                .filter(option => option?.value !== '') : 
                            []
                    }
                    onChange={handleCancelReasonChange}
                />

                <CentralSelect 
                    value={primaryOptions.length ? primaryReason : ''}
                    label="Primary Reason *"
                    options={primaryOptions.length ? primaryOptions : []}
                    onChange={handlePrimaryReasonChange}
                    disabled={!cancelReason}
                />

                <CentralSelect 
                    value={secondaryOptions.length ? secondaryReason : ''}
                    label="Secondary Reason *"
                    options={secondaryOptions.length ? secondaryOptions : []}
                    onChange={(e) => setSecondaryReason(e?.target?.value || '')}
                    disabled={!primaryReason}
                />
            </div>
        </CentralModal>
    );
};
